import React, { useState, useEffect } from 'react';
import { Users, Search, RefreshCw, Filter, Building2 } from 'lucide-react';
import { api } from '../../utils/api';

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'SYSTEM_ADMIN', label: 'System Admin' },
  { value: 'CLIENT_ADMIN', label: 'Client Admin' },
  { value: 'SCHEDULER', label: 'Scheduler' },
  { value: 'RECORD_KEEPER', label: 'Record Keeper' },
  { value: 'DRIVER', label: 'Driver' },
  { value: 'OWNER_DRIVER', label: 'Owner/Driver' },
  { value: 'GROUP_VIEWER', label: 'Group Viewer' },
  { value: 'VIEWER', label: 'Viewer' },
];

const ROLE_COLORS = {
  SYSTEM_ADMIN: 'bg-red-500/20 text-red-400',
  CLIENT_ADMIN: 'bg-blue-500/20 text-blue-400',
  SCHEDULER: 'bg-cyan-500/20 text-cyan-400',
  RECORD_KEEPER: 'bg-amber-500/20 text-amber-400',
  DRIVER: 'bg-emerald-500/20 text-emerald-400',
  OWNER_DRIVER: 'bg-emerald-500/20 text-emerald-400',
  GROUP_VIEWER: 'bg-violet-500/20 text-violet-400',
  VIEWER: 'bg-slate-500/20 text-slate-400',
};

const AllUsersPanel = ({ onViewClient }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    loadUsers();
  }, [roleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.set('role', roleFilter);
      const data = await api(`/sysadmin/users?${params.toString()}`);
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.first_name && u.first_name.toLowerCase().includes(q)) ||
      (u.last_name && u.last_name.toLowerCase().includes(q)) ||
      (u.client_name && u.client_name.toLowerCase().includes(q))
    );
  });

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div data-testid="sysadmin-users-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">All Users</h2>
          <p className="text-sm text-slate-400">{filtered.length} of {users.length} users</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto flex-wrap">
          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm appearance-none cursor-pointer focus:outline-none focus:border-violet-500"
              data-testid="users-role-filter"
            >
              {ROLE_OPTIONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          {/* Search */}
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-56 pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-violet-500"
              data-testid="users-search"
            />
          </div>
          <button onClick={loadUsers} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition" data-testid="users-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-4 text-slate-400 font-medium">User</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden sm:table-cell">Role(s)</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden md:table-cell">Client</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden lg:table-cell">Status</th>
                  <th className="text-left p-4 text-slate-400 font-medium hidden lg:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.user_id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-medium">
                            {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-white font-medium truncate">{u.first_name} {u.last_name}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                          {/* Mobile role badge */}
                          <div className="flex gap-1 mt-1 sm:hidden">
                            {u.roles.map(r => (
                              <span key={r} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ROLE_COLORS[r] || 'bg-slate-600/20 text-slate-400'}`}>
                                {r.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden sm:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.length > 0 ? u.roles.map(r => (
                          <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[r] || 'bg-slate-600/20 text-slate-400'}`}>
                            {r.replace('_', ' ')}
                          </span>
                        )) : (
                          <span className="text-xs text-slate-500 italic">No role</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      {u.client_id ? (
                        <button
                          onClick={() => onViewClient({ client_id: u.client_id, name: u.client_name })}
                          className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 transition text-xs"
                          data-testid={`user-client-link-${u.user_id}`}
                        >
                          <Building2 className="w-3 h-3" />
                          <span className="truncate max-w-[140px]">{u.client_name}</span>
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500">-</span>
                      )}
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
                      }`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="p-4 hidden lg:table-cell text-slate-400 text-xs">
                      {formatDate(u.created_at)}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500">
                      {search || roleFilter ? 'No users matching your filters' : 'No users found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsersPanel;
